import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileSignature, FileCheck2, Download, X } from "lucide-react";
import { fmtDate } from "../crm/shared";
import api from "@/lib/api";
import { InternalSignaturePad } from "@/components/InternalSignaturePad";

export default function PortalContracts({ token, contracts, onChanged }) {
    const [searchParams] = useSearchParams();
    const signingContractId = searchParams.get("firma");
    const signingToken = searchParams.get("st");
    const [preview, setPreview] = useState(() => contracts.find((item) => item.id === signingContractId) || null);
    useEffect(() => {
        if (signingContractId) setPreview(contracts.find((item) => item.id === signingContractId) || null);
    }, [contracts, signingContractId]);
    if (contracts.length === 0) return <p className="text-xs text-ink-500">Nessun documento condiviso con te al momento.</p>;
    return (
        <div className="space-y-3">
            {contracts.map((c) => (
                <div key={c.id} data-testid={`portal-contract-${c.id}`} className="border border-white/10 bg-ink-900/40 p-4 flex items-center gap-3 flex-wrap">
                    {c.status === "signed"
                        ? <FileCheck2 size={17} className="text-green-300 shrink-0" />
                        : <FileSignature size={17} className="text-blue-400 shrink-0" />}
                    <div className="flex-1 min-w-[180px]">
                        <div className="text-sm text-ink-50">{c.title}</div>
                        <div className="text-[11px] text-ink-400 mt-0.5">
                            {c.status === "signed"
                                ? `FIRMATO ${fmtDate(c.signature?.signed_at)} da ${c.signature?.signer_name || "il cliente"}`
                                : `Inviato il ${(c.sent_at || "").slice(0, 10)} · in attesa di firma`}
                        </div>
                    </div>
                    {c.status === "signed"
                        ? <span data-testid={`contract-signed-badge-${c.id}`} className="text-[9px] uppercase tracking-wider px-2 py-1 border bg-green-900/30 text-green-300 border-green-700/40">FIRMATO</span>
                        : <span className="text-[9px] uppercase tracking-wider px-2 py-1 border bg-blue-900/25 text-blue-300 border-blue-600/40">Da firmare</span>}
                    <div className="flex gap-2">
                        <button data-testid={`contract-preview-${c.id}`} onClick={() => setPreview(c)}
                            className="cta-outline px-3 py-2 text-xs flex items-center gap-1.5"><FileSignature size={12} /> Leggi</button>
                        <button data-testid={`contract-pdf-${c.id}`}
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/portal/${token}/contracts/${c.id}/pdf`, "_blank")}
                            className="cta-outline px-3 py-2 text-xs flex items-center gap-1.5"><Download size={12} /> PDF</button>
                    </div>
                </div>
            ))}
            {preview && (
                <PreviewModal
                    token={token}
                    contract={preview}
                    signingToken={preview.id === signingContractId ? signingToken : null}
                    onClose={() => setPreview(null)}
                    onSigned={async () => { setPreview(null); await onChanged?.(); }}
                />
            )}
        </div>
    );
}

function PreviewModal({ token, contract, signingToken, onClose, onSigned }) {
    const [details, setDetails] = useState(null);
    useEffect(() => {
        document.body.classList.add("signature-modal-open");
        return () => document.body.classList.remove("signature-modal-open");
    }, []);
    useEffect(() => {
        api.get(`/portal/${token}/contracts/${contract.id}/content`).then((response) => setDetails(response.data)).catch(() => setDetails(null));
    }, [token, contract.id]);
    return (
        <div className="fixed inset-0 z-[60]">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <div data-testid="contract-preview-modal" className="absolute inset-x-0 bottom-0 sm:inset-0 sm:m-auto sm:h-fit sm:max-w-xl bg-ink-950 border border-white/15 max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-ink-950/95 backdrop-blur border-b border-white/10 px-5 py-4 flex items-center justify-between">
                    <h3 className="font-serif text-lg text-ink-50 truncate pr-4">{contract.title}</h3>
                    <button data-testid="contract-preview-close" onClick={onClose} className="text-ink-400 hover:text-ink-50"><X size={18} /></button>
                </div>
                <div className="p-5">
                    {contract.status === "signed" ? (
                        <div data-testid="contract-signed-status" className="text-center py-6">
                            <FileCheck2 size={36} className="text-green-300 mx-auto mb-4" />
                            <h4 className="font-serif text-2xl text-ink-50">Documento firmato</h4>
                            <p className="text-ink-400 text-sm mt-2 mb-6">
                                {(contract.signature_method === "internal_graphic" || contract.signature?.method === "internal_graphic")
                                    ? "Firma grafica elettronica apposta"
                                    : "Documento sottoscritto"} da {contract.signature?.signer_name || "il cliente"} {fmtDate(contract.signature?.signed_at)}.
                            </p>
                            <button data-testid="signed-pdf-btn"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/portal/${token}/contracts/${contract.id}/pdf`, "_blank")}
                                className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 text-sm rounded-full transition-colors">Scarica PDF firmato</button>
                        </div>
                    ) : (
                        <div data-testid="contract-pending-status" className="space-y-5">
                            <div className="contract-document-preview" data-testid={`contract-content-${contract.id}`}>
                                <div className="text-[10px] uppercase tracking-wider text-gold mb-3">Documento da sottoscrivere</div>
                                <div className="whitespace-pre-wrap text-sm leading-7 text-ink-100">{details?.content || "Caricamento documento…"}</div>
                            </div>
                            <button data-testid="contract-preview-pdf-btn"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/portal/${token}/contracts/${contract.id}/pdf`, "_blank")}
                                className="cta-outline px-5 py-2.5 text-xs flex items-center gap-2"><Download size={12} /> Apri anteprima PDF</button>
                            <InternalSignaturePad
                                token={token}
                                contractId={contract.id}
                                signingToken={signingToken}
                                onSigned={onSigned}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
